import React, { CSSProperties, ReactElement } from 'react';

interface PngTemplateArtProps {
  src: string;
  isDarkMode: boolean;
  alt?: string;
}

/**
 * Renders a PNG asset using CSS blend modes so the near-white background
 * becomes visually transparent against any canvas background:
 *
 * Light mode  — mix-blend-mode: multiply
 *   near-white (247,248,250) × white-canvas ≈ white (invisible)
 *   coloured / dark pixels stay visible
 *
 * Dark mode — filter: invert(1) + mix-blend-mode: screen
 *   near-white → near-black after invert → screen on dark canvas ≈ invisible
 *   dark lines → light after invert → visible against dark background
 *
 * No true alpha channel required; works with every existing linear-*.png asset.
 */
export default function PngTemplateArt({ src, isDarkMode, alt = '' }: PngTemplateArtProps): ReactElement {
  const style: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'fill',
    userSelect: 'none',
    pointerEvents: 'none',
    mixBlendMode: isDarkMode ? 'screen' : 'multiply',
    filter: isDarkMode ? 'invert(1)' : undefined,
  };

  return <img src={src} alt={alt} draggable={false} aria-hidden style={style} />;
}
